import { Box, Grid, Stack, Typography } from '@mui/material';
import PageHeader from 'components/Header/Page';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import ClusterIcon from '../../assets/Cluster Analytics.svg';
import moment from 'moment';
import ReactECharts, { EChartsOption } from 'echarts-for-react';
import {
    countByCategoryData,
    costByClusterData,
    clusterSpinsUpData,
    computeJobData,
    computeInteractiveData,
    workspaceGetEnv,
    clusterLastSyncData,
    countByTypeData,
    costByTypeData,
    withoutAUtoTermcountByTypeData,
    fetchSectorList
} from 'service/service';
import LoaderComponent from 'components/Loader';
import PageWrapper from 'components/Common/PageWraper';
import NumberWidget from 'components/Widgets/NumberWidget';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import FiltersBar from './FilterBar';
import { useLocation, useNavigate } from 'react-router-dom';
import { toTitleCase } from './workspace';
import { useAdbBaseState } from 'utils/adb-common-util';

interface Row {
    storageAccount: string;
    resourceGroup: string;
    size: string;
    monthlyCost: string;
    growth: string;
    owner: string;
}

const formatLastSync = (lastSync: string | null) => {
    if (!lastSync) return 'Loading...';
    const date = new Date(lastSync);
    const options = { timeZone: 'UTC' } as const;
    const weekday = date.toLocaleString('en-US', { ...options, weekday: 'short' });
    const day = date.toLocaleString('en-US', { ...options, day: '2-digit' });
    const month = date.toLocaleString('en-US', { ...options, month: 'short' });
    const year = date.toLocaleString('en-US', { ...options, year: 'numeric' });
    return `${weekday}, ${day} ${month} ${year}(UTC)`;
};

const ClusterAnalytics = () => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const navigationState = routerLocation.state as {
        startDate?: string;
        endDate?: string;
        env?: string;
        sector?: string;
        location?: string;
        selectedDateInString?: string;
    } | null;

    const {
        restoredRef,
        prevParamsRef,
        PRESERVE_FILTER_KEY,
        yesterday,
        defStartDate,
        ...defaults
    } = useAdbBaseState(navigationState, 'cluster_analytics');
    const DEFAULT_FILTERS = defaults;

    const [startDate, setStartDate] = useState<string>(DEFAULT_FILTERS.startDate);
    const [endDate, setEndDate] = useState<string>(DEFAULT_FILTERS.endDate);
    const [selectedDateInString, setSelectedDateInString] = useState<string>(DEFAULT_FILTERS.selectedDateInString);
    const [sectorName, setSectorName] = useState(DEFAULT_FILTERS.sector);
    const [sector, setSector] = useState<string>(DEFAULT_FILTERS.sector);
    const [env, setEnv] = useState<string>(() => navigationState?.env || 'both');
    const [lastSync, setLastSync] = useState(null);
    const [envOptions, setEnvOptions] = useState<{ label: string; value: string }[]>([]);
    const [sectorList, setSectorList] = useState<string[]>([]);

    const fetchSectors = async () => {
        const resp = await fetchSectorList({});
        if (resp?.success) {
            setSectorList(resp?.data || []);
        }
    };

    useEffect(() => {
        fetchSectors()
    }, [])

    // refs to help with env fallback logic
    const initialEnvAppliedRef = useRef(false);
    const envRef = useRef(env);
    useEffect(() => { envRef.current = env; }, [env]);

    const applyNonDateFilters = (navState: any) => {
        if (navState.env) setEnv(navState.env);
        if (navState.sector) {
            setSector(navState.sector);
            setSectorName(navState.sector);
        }
        if (navState.location) setLocation(navState.location);
    };

    const setDateFilters = (data: { startDate?: string; endDate?: string; selectedDateInString?: string }) => {
        if (data.startDate) setStartDate(data.startDate);
        if (data.endDate) setEndDate(data.endDate);
        if (data.selectedDateInString) setSelectedDateInString(data.selectedDateInString);
    };

    const applyDateFilters = (navState: any, preservedRaw: string | null) => {
        const token = navState.selectedDateInString?.trim() || '';
        const isCustom = ['custom', 'customDateRange'].includes(token);

        if (preservedRaw && isCustom) {
            const p = JSON.parse(preservedRaw);
            setDateFilters(p);
            restoredRef.current = true;
            return;
        }

        if (preservedRaw) return;

        if (isCustom) {
            setStartDate(defStartDate.format('YYYY-MM-DD'));
            setEndDate(yesterday.clone().endOf('day').format('YYYY-MM-DD'));
            setSelectedDateInString('3M');
            return;
        }

        setDateFilters(navState);
    };

    useEffect(() => {
        const preservedRaw = sessionStorage.getItem(PRESERVE_FILTER_KEY);
        if (!navigationState) return;

        applyNonDateFilters(navigationState);
        applyDateFilters(navigationState, preservedRaw);

        initialEnvAppliedRef.current = false;
        // clear prevParams so the first fetch after restore runs; delay clearing until state updates flush
        setTimeout(() => {
            prevParamsRef.current = '';
        }, 0);
    }, []);

    const handleDateSelect = (stDt: string, edDt: string, selectedDate: string = 'Hrs') => {
        setSelectedDateInString(selectedDate);
        const yesterday = moment.tz('Asia/Kolkata').tz('Asia/Kolkata').subtract(1, 'day');
        const end = yesterday.endOf('day');
        let start;

        if (selectedDate === 'Hrs') {
            start = yesterday.startOf('day');
        } else if (selectedDate === '7d') {
            start = yesterday.clone().subtract(6, 'days').startOf('day');
        } else if (selectedDate === '30d') {
            start = yesterday.clone().subtract(29, 'days').startOf('day');
        } else if (selectedDate === 'custom') {
            start = moment.tz(stDt, 'Asia/Kolkata').startOf('day');
        } else {
            start = moment.tz(stDt, 'Asia/Kolkata').startOf('day');
        }
        setStartDate(start.format('YYYY-MM-DD'));
        setEndDate(end.format('YYYY-MM-DD'));
    };

    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 2
    });
    const [location, setLocation] = useState('South Central US');
    const compactFormatter = new Intl.NumberFormat('en-US', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: 2
    });
    const [loader, setLoader] = useState({
        clusterType: false,
        spentByCategoryType: false,
        costType: false,
        SpinsUp: false,
        computeJobType: false,
        computeInteractiveType: false,
        env: false,
    });
    const [totalCountByCluster, setTotalCountByCluster] = useState<
        [
            {
                _id: null;
                total_interactive_count: number;
                total_job_count: number;
                total_job_new_count: number;
                total_interactive_new_count: number;
                total_without_autoTerm_interactive_count: number;
                total_without_autoTerm_job_count: number;
            }
        ]
    >();
    const [totalSpentByCategory, setTotalSpentByCategory] = useState<[{ name: string, value: number }]>();
    const [totalCostByCluster, setTotalCostByCluster] = useState<
        [
            {
                totalSpend: number;
                date: string;
            }
        ]
    >();
    const [totalSpinsUp, setTotalSpinsUp] = useState<
        [
            {
                totalInteractiveSpinnedCount: number;
                totalJobSpinnedCount: number;
                date: string;
            }
        ]
    >();
    const [computeJob, setComputeJob] = useState<
        [
            {
                _id: string;
                clusterSpend: number;
            }
        ]
    >();
    const [computeInteractive, setComputeInteractive] = useState<
        [
            {
                _id: string;
                clusterSpend: number;
            }
        ]
    >();
    const [cCount, setCCount] = useState<[{ name: string, value: number }]>();
    const [withoutAutoTermCount, setWithoutAutoTermCount] = useState<[{ name: string, value: number }]>();

    const filteredCCount = useMemo(() => {
        return (Array.isArray(cCount) ? cCount : []).filter((curr) => curr.name !== "SQL");
    }, [cCount]);

    const totalCCount = useMemo(() => {
        return filteredCCount.reduce((acc, curr) => acc + curr.value, 0);
    }, [filteredCCount]);

    const filteredSpentByCategory = useMemo(() => {
        return (Array.isArray(totalSpentByCategory) ? totalSpentByCategory : []).filter((curr) => curr.name !== "SQL");
    }, [totalSpentByCategory]);

    const fetchLastSync = async () => {
        const res = await clusterLastSyncData();
        if (res?.success && res?.statusCode === 200) {
            const date = res.data?.End_Time;
            setLastSync(date);
        }
    };

    const formattedLastSync = formatLastSync(lastSync);

    const CountByCategory = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            clusterType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await countByCategoryData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setTotalCountByCluster(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            clusterType: false
        }));
    };
    const spentByCategoryFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            spentByCategoryType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await costByTypeData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setTotalSpentByCategory(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            spentByCategoryType: false
        }));
    };

    const CostByClusterFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            costType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await costByClusterData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success && resp.data) {
            setTotalCostByCluster(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            costType: false
        }));
    };

    const clusterSpinsUpFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            SpinsUp: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await clusterSpinsUpData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setTotalSpinsUp(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            SpinsUp: false
        }));
    };

    const computeJobFetch = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            computeJobType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await computeJobData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setComputeJob(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            computeJobType: false
        }));
    };
    const computeInteractiveFetch = async (
        startDate: any,
        endDate: any,
        env: string,
        sector: string,
        location: string,
        selectedDateInString: string
    ) => {
        setLoader((prev) => ({
            ...prev,
            computeInteractiveType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await computeInteractiveData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setComputeInteractive(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            computeInteractiveType: false
        }));
    };

    const ComputeCount = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            countByType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await countByTypeData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setCCount(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            countByType: false
        }));
    };

    const ComputeWithoutAutoTermCount = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
        setLoader((prev) => ({
            ...prev,
            countByType: true
        }));
        const startFormatted = startDate.split(' ')[0];
        const endFormatted = endDate.split(' ')[0];

        const resp = await withoutAUtoTermcountByTypeData(startFormatted, endFormatted, env, sector, location, selectedDateInString);
        if (resp?.success) {
            setWithoutAutoTermCount(resp?.data);
        }
        setLoader((prev) => ({
            ...prev,
            countByType: false
        }));
    };

    const ClusterByCategory: EChartsOption = {
        color: ['#01C008', '#299BFF'],
        backgroundColor: '#ffffff',

        graphic: {
            type: 'text',
            left: '15%',
            top: 'center',
            style: {
                text: `Total: ${compactFormatter.format(totalCCount)}`,
                fill: '#000000',
                font: 'bold 20px sans-serif',
                align: 'center',
                verticalAlign: 'middle'
            }
        },

        tooltip: {
            backgroundColor: '#ffffff',
            trigger: 'item',
            textStyle: {
                color: '#000000'
            },
            formatter: () => {
                const total = totalCCount;
                const lines = filteredCCount.map((item) => {
                    return `<div><strong style="color:#000">${toTitleCase(item.name)}</strong>: ${compactFormatter.format(
                        item.value
                    )}</div>`;
                });

                return (
                    `<div style="margin-bottom: 6px;"><strong style="color:#000">Total: ${compactFormatter.format(
                        total
                    )}</strong></div>` + lines.join('')
                );
            }
        },

        legend: {
            orient: 'vertical',
            right: '3%',
            top: 'center',
            left: 'auto',
            formatter: (name: string) => toTitleCase(name),
            data: filteredCCount.map((item) => item.name),
            textStyle: {
                color: '#000000',
                fontSize: 15
            }
        },

        series: [
            {
                type: 'pie',
                center: ['30%', '50%'],
                radius: ['60%', '78%'],
                avoidLabelOverlap: false,
                label: {
                    show: true,
                    position: 'inside',
                    color: '#fff',
                    fontSize: 15,
                    formatter: (params: any) => {
                        const value = Number(params.value ?? 0);
                        return `${compactFormatter.format(value)}`;
                    }
                },
                labelLine: {
                    show: false
                },
                data: filteredCCount.map((item) => ({
                    name: item.name,
                    value: item.value
                }))
            }
        ]
    };
    const colorsMap: Record<string, string> = {
        INTERACTIVE: '#299BFF',
        JOB: '#01C008'
    };
    const SpendByCategory: EChartsOption = {
        backgroundColor: '#ffffff',
        tooltip: {
            textStyle: {
                color: '#000000'
            },
            backgroundColor: '#ffffff',
            trigger: 'axis',
            formatter: function (params: any) {
                return params
                    .map((item: { marker: any; seriesName: any; name: any; value: any }) => {
                        return `${item.marker} ${toTitleCase(item.name)}: ${formatter.format(item.value)}`;
                    })
                    .join('<br/>');
            }
        },
        xAxis: {
            type: 'category',
            data: filteredSpentByCategory.map((item) => item.name),
            axisLabel: {
                color: '#000000',
                fontSize: 15,
                formatter: (value: string) => toTitleCase(value)
            },
            splitLine: {
                show: false
            }
        },
        yAxis: {
            type: 'value',
            axisLine: {
                show: true
            },
            axisLabel: {
                color: '#000000',
                fontSize: 10,
                formatter: (value: number) => `$${compactFormatter.format(value)}`
            },
            splitLine: {
                show: false
            }
        },
        series: [
            {
                data: filteredSpentByCategory.map((item) => ({
                    value: item.value,
                    itemStyle: { color: colorsMap[item.name] || '#FFFFFF' }
                })),
                type: 'bar',
                barWidth: 50,
                label: {
                    show: true,
                    position: 'inside',
                    color: '#fff',
                    fontSize: 10,
                    formatter: (params: any) => {
                        const value = Number(params.value ?? 0);
                        return `${formatter.format(value)}`;
                    }
                }
            }
        ]
    };
    const xAxisLabels = totalCostByCluster?.map((item: { date: string }) => item.date) ?? [];
    const seriesData = Array.isArray(totalCostByCluster) && totalCostByCluster.length > 0
        ? totalCostByCluster.map((item: { totalSpend: number }) => item.totalSpend)
        : [];

    const CostByCluster: EChartsOption = {
        backgroundColor: '#ffffff',
        tooltip: {
            backgroundColor: '#ffffff',
            textStyle: {
                color: '#000000'
            },
            trigger: 'axis',
            formatter: function (params: any) {
                return params
                    .map((item: { marker: any; seriesName: any; name: any; value: any }) => {
                        return `${item.marker} ${item.name}: $${item.value}`;
                    })
                    .join('<br/>');
            }
        },
        grid: {
            left: 50,
            right: 20,
            top: 60,
            bottom: 60
        },
        xAxis: {
            type: 'category',
            data: xAxisLabels,
            name: 'Date',
            nameLocation: 'middle',
            nameGap: 30,
            nameTextStyle: {
                color: '#000000',
                fontSize: 12,
                fontWeight: 'normal'
            },
            axisLine: {
                lineStyle: { color: '#71758FCC' }
            },
            axisLabel: {
                color: '#000000'
            },
            splitLine: {
                show: false
            }
        },
        yAxis: {
            type: 'value',
            axisLine: {
                show: true
            },
            axisLabel: {
                color: '#000000',
                formatter: (value: number) => `$${compactFormatter.format(value)}`
            },
            splitLine: {
                show: false
            }
        },
        series: [
            {
                data: seriesData,
                type: 'line',
                showSymbol: false,
                itemStyle: {
                    color: '#FE2626'
                },
                lineStyle: {
                    color: '#FE2626'
                }
            }
        ]
    };

    const provisionedArray = [
        { name: 'Interactive', value: totalCountByCluster?.[0]?.total_interactive_new_count ?? 0 },
        { name: 'Job', value: totalCountByCluster?.[0]?.total_job_new_count ?? 0 }
    ];
    const provisionedByCategory: EChartsOption = {
        grid: {
            left: 60,
        },
        backgroundColor: '#FFFFFF',
        tooltip: {
            textStyle: {
                color: '#000000'
            },
            backgroundColor: '#FFFFFF',
            trigger: 'axis',
            formatter: function (params: any) {
                return params
                    .map((item: { marker: any; seriesName: any; name: any; value: any }) => {
                        return `${item.marker} ${item.name}: ${compactFormatter.format(item.value)}`;
                    })
                    .join('<br/>');
            }
        },
        xAxis: {
            data: ['Job', 'Interactive'],
            axisLabel: {
                color: '#000000',
                fontSize: 15,
                formatter: (value: string) => toTitleCase(value)
            },
            splitLine: {
                show: false
            }
        },
        yAxis: {
            type: 'value',
            name: 'Count',
            nameLocation: 'middle',
            nameRotate: 90,
            nameGap: 40,
            nameTextStyle: {
                color: '#000000',
                fontSize: 12,
                fontWeight: 'normal'
            },
            axisLine: {
                show: true
            },
            axisLabel: {
                color: '#000000',
                fontSize: 10,
                formatter: (value: number) => compactFormatter.format(value)
            },
            splitLine: {
                show: false
            }
        },
        series: [
            {
                data: [
                    { value: provisionedArray[1].value, itemStyle: { color: '#01C008' } },
                    { value: provisionedArray[0].value, itemStyle: { color: '#299BFF' } }
                ],
                type: 'bar',
                barWidth: 50,
                label: {
                    show: true,
                    position: 'inside',
                    color: '#fff',
                    fontSize: 10,
                    formatter: (params: any) => {
                        const value = Number(params.value ?? 0);
                        return `${compactFormatter.format(value)}`;
                    }
                }
            }
        ]
    };
    const xAxisLabel = totalSpinsUp?.map((item) => item.date) ?? [];

    const jobSeriesData = totalSpinsUp?.map((item) => item.totalJobSpinnedCount) ?? [];

    const interactiveSeriesData = totalSpinsUp?.map((item) => item.totalInteractiveSpinnedCount) ?? [];

    const ClusterSpinsUps: EChartsOption = {
        backgroundColor: '#ffffff',
        tooltip: {
            backgroundColor: '#ffffff',
            textStyle: {
                color: '#000000'
            },
            trigger: 'axis'
        },
        grid: {
            left: 50,
            right: 20,
            top: 60,
            bottom: 60
        },
        legend: {
            orient: 'horizontal',
            bottom: 10,
            left: 3,
            data: [
                {
                    name: 'Job',
                    icon: 'path://M2,6 Q2,2 6,2 L28,2 Q32,2 32,6 L32,14 Q32,18 28,18 L6,18 Q2,18 2,14 Z'
                },
                {
                    name: 'Interactive',
                    icon: 'path://M2,6 Q2,2 6,2 L28,2 Q32,2 32,6 L32,14 Q32,18 28,18 L6,18 Q2,18 2,14 Z'
                }
            ],
            textStyle: {
                color: '#000000',
                fontSize: 15
            },
            itemGap: 20
        },
        xAxis: {
            type: 'category',
            data: xAxisLabel,
            name: 'Date',
            nameLocation: 'middle',
            nameGap: 30,
            nameTextStyle: {
                color: '#000000',
                fontSize: 12,
                fontWeight: 'normal'
            },
            axisLine: {
                lineStyle: { color: '#71758FCC' }
            },
            axisLabel: {
                color: '#000000'
            },
            splitLine: {
                show: false
            }
        },
        yAxis: {
            type: 'value',
            name: 'Count',
            nameLocation: 'middle',
            nameRotate: 90,
            nameGap: 30,
            nameTextStyle: {
                color: '#FAFAFA',
                fontSize: 12,
                fontWeight: 'normal'
            },
            axisLine: {
                show: true,
                lineStyle: { color: '#71758FCC' }
            },
            axisLabel: {
                color: '#000000',
                formatter: function (value: number) {
                    if (value === 0) {
                        return '0';
                    } else if (value >= 1_000_000_000) {
                        return Math.round(value / 1_000_000_000) + 'B';
                    } else if (value >= 1_000_000) {
                        return Math.round(value / 1_000_000) + 'M';
                    } else if (value >= 1_000) {
                        return Math.round(value / 1_000) + 'K';
                    } else {
                        return value.toFixed(0);
                    }
                },
            },
            splitLine: {
                show: false
            }
        },
        series: [
            {
                name: 'Job',
                data: jobSeriesData,
                type: 'line',
                itemStyle: {
                    color: '#01C008'
                },
                lineStyle: {
                    color: '#01C008'
                }
            },
            {
                name: 'Interactive',
                data: interactiveSeriesData,
                type: 'line',
                itemStyle: {
                    color: '#299BFF'
                },
                lineStyle: {
                    color: '#299BFF'
                }
            }
        ]
    };
    const sortedJob =
        computeJob && [...computeJob].sort((a, b) => a.clusterSpend - b.clusterSpend);
    const ComputeJob: EChartsOption = {
        backgroundColor: '#ffffff',
        tooltip: {
            backgroundColor: '#ffffff',
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            textStyle: {
                color: '#000000'
            },
            formatter: function (params: any) {
                const dataPoint = params[0];
                return `${dataPoint.name}: $${dataPoint.value}`;
            }
        },
        grid: {
            left: '10%',
            right: '5%',
            bottom: '5%',
            top: '15%',
            containLabel: true
        },
        xAxis: {
            type: 'value',
            axisLabel: {
                show: true,
                formatter: function (value: number) {
                    if (value === 0) {
                        return '0';
                    } else if (value >= 1_000_000_000) {
                        return '$' + (value / 1_000_000_000) + 'B';
                    } else if (value >= 1_000_000) {
                        return '$' + (value / 1_000_000) + 'M';
                    } else if (value >= 1_000) {
                        return '$' + (value / 1_000) + 'K';
                    } else {
                        return '$' + value;
                    }
                },
                fontSize: 10,
                color: '#000000'
            },
            axisLine: {
                show: true
            },
            axisTick: {
                show: true
            },
            splitLine: {
                show: false,
                lineStyle: {
                    color: '#1c3b6b'
                }
            }
        },
        yAxis: {
            type: 'category',
            data: sortedJob?.map((item) => item._id),
            axisLabel: {
                color: '#000000',
                formatter: function (value: any) {
                    return value.length > 10 ? value.substring(0, 10) + '…' : value;
                }
            }
        },
        series: [
            {
                name: 'Spend',
                type: 'bar',
                data: sortedJob?.map((item) => item.clusterSpend),
                itemStyle: {
                    color: '#01C008'
                },
                barWidth: '60%',
                label: {
                    show: true,
                    position: 'inside',
                    color: '#fff',
                    fontSize: 10,
                    formatter: (params: any) => {
                        const value = Number(params.value ?? 0);
                        return `${formatter.format(value)}`;
                    }
                }
            }
        ]
    };

    const sortedInteractive =
        computeInteractive &&
        [...computeInteractive]
            .sort((a, b) => a.clusterSpend - b.clusterSpend);
    const ComputeInteractive: EChartsOption = {
        backgroundColor: '#ffffff',
        tooltip: {
            backgroundColor: '#ffffff',
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            textStyle: {
                color: '#000000'
            },
            formatter: function (params: any) {
                const dataPoint = params[0];
                return `${dataPoint.name}: $${dataPoint.value}`;
            }
        },
        grid: {
            left: '10%',
            right: '5%',
            bottom: '5%',
            top: '15%',
            containLabel: true
        },
        xAxis: {
            type: 'value',
            axisLabel: {
                show: true,
                formatter: function (value: number) {
                    if (value === 0) {
                        return '0';
                    } else if (value >= 1_000_000_000) {
                        return '$' + (value / 1_000_000_000) + 'B';
                    } else if (value >= 1_000_000) {
                        return '$' + (value / 1_000_000) + 'M';
                    } else if (value >= 1_000) {
                        return '$' + (value / 1_000) + 'K';
                    } else {
                        return '$' + value;
                    }
                },
                fontSize: 10,
                color: '#000000'
            },
            axisLine: {
                show: true
            },
            axisTick: {
                show: true
            },
            splitLine: {
                show: false,
                lineStyle: {
                    color: '#1c3b6b'
                }
            }
        },
        yAxis: {
            type: 'category',
            data: sortedInteractive?.map((item) => item._id),
            axisLabel: {
                color: '#000000',
                formatter: function (value: any) {
                    return value.length > 10 ? value.substring(0, 10) + '…' : value;
                }
            }
        },
        series: [
            {
                name: 'Spend',
                type: 'bar',
                data: sortedInteractive?.map((item) => item.clusterSpend),
                itemStyle: {
                    color: '#299BFF'
                },
                barWidth: '60%',
                label: {
                    show: true,
                    position: 'inside',
                    color: '#fff',
                    fontSize: 10,
                    formatter: (params: any) => {
                        const value = Number(params.value ?? 0);
                        return `${formatter.format(value)}`;
                    }
                }
            }
        ]
    };

    const getEnvFetch = async (location: string) => {
        // preserve incoming navigation state env when available (apply once)
        try {
            setLoader((prev) => ({
                ...prev,
                env: true
            }));
            const res = await workspaceGetEnv(location);
            const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
            setEnvOptions(options);
            // if navigation provided an env, apply it once; otherwise ensure current env is valid
            if (!initialEnvAppliedRef.current && navigationState?.env) {
                setEnv(navigationState.env);
                initialEnvAppliedRef.current = true;
            } else {
                const availableValues = options.map((o) => o.value);
                const current = envRef.current;
                if (!availableValues.includes(current)) {
                    const fallback = availableValues.includes('both') ? 'both' : (availableValues[0] || 'both');
                    setEnv(fallback);
                }
            }
        } catch (error) {
            console.error('Failed to fetch environment options:', error);
        } finally {
            setLoader((prev) => ({
                ...prev,
                env: false
            }));
        }
    };

    useEffect(() => {
        if (!(startDate && endDate && env && sector && location)) return;

        const currentKey = JSON.stringify({ startDate, endDate, env, sector, location, selectedDateInString });
        // if we've already fetched for this exact params set, skip
        if (prevParamsRef.current === currentKey) return;
        prevParamsRef.current = currentKey;

        let mounted = true;
        const runAll = async () => {
            try {
                await Promise.all([
                    CountByCategory(startDate, endDate, env, sector, location, selectedDateInString),
                    spentByCategoryFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    CostByClusterFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    clusterSpinsUpFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    computeJobFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    computeInteractiveFetch(startDate, endDate, env, sector, location, selectedDateInString),
                    ComputeCount(startDate, endDate, env, sector, location, selectedDateInString),
                    ComputeWithoutAutoTermCount(startDate, endDate, env, sector, location, selectedDateInString),
                    fetchLastSync()
                ]);
            } catch (err) {
                console.error('Workspace fetches failed', err);
            } finally {
                if (mounted && restoredRef.current) {
                    sessionStorage.removeItem(PRESERVE_FILTER_KEY);
                    restoredRef.current = false;
                }
            }
        };

        runAll();
        return () => { mounted = false; };
    }, [startDate, endDate, env, sector, location, selectedDateInString]);

    useEffect(() => {
        getEnvFetch(location);
    }, [location]);


    const updateHistoryState = (overrides: Partial<{
        startDate: string;
        endDate: string;
        env: string;
        sector: string;
        location: string;
        selectedDateInString: string;
    }>) => {
        const newState = {
            startDate,
            endDate,
            env,
            sector: sectorName,
            location,
            selectedDateInString,
            ...overrides
        };
        navigate(routerLocation.pathname, { state: newState, replace: true });
    };


    const handleCountClusterOnClick = (chartType: any, params: any) => {
        let detailedTableTitle = 'Total Count Cluster By Category Details';
        let detailedTableType = 'total-count-cluster-by-category-detail';
        let pageSubHeader = 'Total Count Cluster By Category Details';
        let newState: any = {
            startDate,
            endDate,
            env,
            sector: sectorName,
            location,
            selectedDateInString,
            searchSource: chartType,
            detailedTableTitle: detailedTableTitle,
            detailedTableType: detailedTableType,
            pageHeader: 'Cluster Analytics',
            pageSubHeader: pageSubHeader,
        };
        if (chartType === 'totalCountClusterByCategory') {
            newState = {
                ...newState,
                contentName: params?.name,
                filters: [
                    {
                        operator: 'AND',
                        field: 'Compute Type',
                        value: params?.name,
                    }
                ]
            };
        }
        localStorage.removeItem(detailedTableType);
        const snap = {
            startDate,
            endDate,
            selectedDateInString,
        };
        sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
        navigate('/databricksADB/performance-dashboard', { state: newState });
    }

    const handleCostSpendOnClick = (chartType: any, params: any) => {
        let detailedTableTitle = 'Cost Spend By Category';
        let detailedTableType = 'cost-spend-by-category-detail';
        let pageSubHeader = 'Cost Spend By Category';
        let newState: any = {
            startDate,
            endDate,
            env,
            sector: sectorName,
            location,
            selectedDateInString,
            searchSource: chartType,
            detailedTableTitle: detailedTableTitle,
            detailedTableType: detailedTableType,
            pageHeader: 'Cluster Analytics',
            pageSubHeader: pageSubHeader,
        };
        if (chartType === 'costSpendByCategory') {
            newState = {
                ...newState,
                contentName: params?.name,
                filters: [
                    {
                        operator: 'AND',
                        field: 'Compute Type',
                        value: params?.name,
                    }
                ]
            };
        }
        localStorage.removeItem(detailedTableType);
        const snap = {
            startDate,
            endDate,
            selectedDateInString,
        };
        sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
        navigate('/databricksADB/performance-dashboard', { state: newState });
    }

    const handleCostByClusterOnClick = (chartType: any, params: any) => {
        const payload = Array.isArray(params) ? params[0] : params;
        const clickedDate = payload?.name || payload?.data?.date || payload?.data?.dateLabel || null;
        if (!clickedDate) return;

        const parsed = moment(clickedDate);
        const formatted = parsed.isValid() ? parsed.format('YYYY-MM-DD') : clickedDate;

        const tzNow = moment().tz('Asia/Kolkata');
        const yesterday = moment().tz('Asia/Kolkata').subtract(1, 'day');
        const isCurrentMonth = parsed.isValid() && parsed.isSame(tzNow, 'month') && parsed.isSame(tzNow, 'year');

        const clickedStartDate = parsed.isValid() ? parsed.clone().startOf('month').format('YYYY-MM-DD') : formatted;
        let clickedEndDate;
        if (parsed.isValid()) {
            if (isCurrentMonth) {
                clickedEndDate = yesterday.clone().format('YYYY-MM-DD');
            } else {
                clickedEndDate = parsed.clone().endOf('month').format('YYYY-MM-DD');
            }
        } else {
            clickedEndDate = formatted;
        }

        let detailedTableTitle = 'Total Cost by Clusters Without Auto-Termination';
        let detailedTableType = 'total-cost-by-clusters-without-auto-termination-detail';
        let pageSubHeader = 'Total Cost by Clusters Without Auto-Termination';
        let newState: any = {
            startDate: formatted,
            endDate: formatted,
            clickedStartDate,
            clickedEndDate,
            env,
            sector: sectorName,
            location,
            selectedDateInString: 'customDateRange',
            searchSource: chartType,
            detailedTableTitle: detailedTableTitle,
            detailedTableType: detailedTableType,
            pageHeader: 'Cluster Analytics',
            pageSubHeader: pageSubHeader,
        };
        if (chartType === 'totalCostByClusterWithAutoTermination') {
            newState = {
                ...newState,
                contentName: params?.name,
                filters: [
                    {
                        operator: 'AND',
                        field: 'Auto Termination Minutes',
                        value: 0,
                    }
                ]
            };
        }
        localStorage.removeItem(detailedTableType);
        const snap = {
            startDate: formatted,
            endDate: formatted,
            selectedDateInString: 'customDateRange',
        };
        sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
        navigate('/databricksADB/performance-dashboard', { state: newState });
    };

    const handleClusterCountOnClick = (chartType: any) => {
        let detailedTableTitle = 'Clusters Count Without Auto-Termination';
        let detailedTableType = 'clusters-count-without-auto-termination-detail';
        let pageSubHeader = 'Clusters Count Without Auto-Termination';
        let newState: any = {
            startDate,
            endDate,
            env,
            sector: sectorName,
            location,
            selectedDateInString,
            searchSource: chartType,
            detailedTableTitle: detailedTableTitle,
            detailedTableType: detailedTableType,
            pageHeader: 'Cluster Analytics',
            pageSubHeader: pageSubHeader,
        };
        if (chartType === 'clustersCountWithoutAutoTermination') {
            newState = {
                ...newState,
                contentName: 'INTERACTIVE',
                filters: [
                    {
                        operator: 'AND',
                        field: 'Compute Type',
                        value: 'INTERACTIVE',
                    },
                    {
                        operator: 'AND',
                        field: 'Auto Termination Minutes',
                        value: 0,
                    }
                ]
            };
        }
        localStorage.removeItem(detailedTableType);
        const snap = {
            startDate,
            endDate,
            selectedDateInString,
        };
        sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
        navigate('/databricksADB/performance-dashboard', { state: newState });
    }

    const handleClusterSpinsUpOnClick = (chartType: any, params: any) => {
        const clickedCategory = params?.seriesName ?? params?.name;
        const computeType = (clickedCategory || '').toString().trim().toUpperCase();
        const payload = Array.isArray(params) ? params[0] : params;
        const clickedDate = payload?.name || payload?.data?.date || payload?.data?.dateLabel || null;
        if (!clickedDate) return;

        const parsed = moment(clickedDate);
        const formatted = parsed.isValid() ? parsed.format('YYYY-MM-DD') : clickedDate;

        const tzNow = moment().tz('Asia/Kolkata');
        const yesterday = moment().tz('Asia/Kolkata').subtract(1, 'day');
        const isCurrentMonth = parsed.isValid() && parsed.isSame(tzNow, 'month') && parsed.isSame(tzNow, 'year');

        const clickedStartDate = parsed.isValid() ? parsed.clone().startOf('month').format('YYYY-MM-DD') : formatted;
        let clickedEndDate;
        if (parsed.isValid()) {
            if (isCurrentMonth) {
                clickedEndDate = yesterday.clone().format('YYYY-MM-DD');
            } else {
                clickedEndDate = parsed.clone().endOf('month').format('YYYY-MM-DD');
            }
        } else {
            clickedEndDate = formatted;
        }

        let detailedTableTitle = 'Clusters Spin-Ups by Category Details';
        let detailedTableType = 'clusters-spin-ups-by-category-detail';
        let pageSubHeader = 'Clusters Spin-Ups by Category Details';
        let newState: any = {
            startDate: formatted,
            endDate: formatted,
            clickedStartDate,
            clickedEndDate,
            env,
            sector: sectorName,
            location,
            selectedDateInString: 'customDateRange',
            searchSource: chartType,
            detailedTableTitle: detailedTableTitle,
            detailedTableType: detailedTableType,
            pageHeader: 'Cluster Analytics',
            pageSubHeader: pageSubHeader,
        };
        if (chartType === 'clusterSpinUpsByCategory') {
            newState = {
                ...newState,
                contentName: params?.name,
                filters: [
                    {
                        operator: 'AND',
                        field: 'Compute Type',
                        value: computeType,
                    }
                ]
            };
        }
        localStorage.removeItem(detailedTableType);
        const snap = {
            startDate: formatted,
            endDate: formatted,
            selectedDateInString: 'customDateRange',
        };
        sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
        navigate('/databricksADB/performance-dashboard', { state: newState });
    };

    const handleCostByComputeJobOnClick = (chartType: any, params: any) => {
        let detailedTableTitle = 'Top 10 Cluster Cost by Compute - Job';
        let detailedTableType = 'top-ten-cluster-cost-by-compute-job-detail';
        let pageSubHeader = 'Top 10 Cluster Cost by Compute - Job';
        let newState: any = {
            startDate,
            endDate,
            env,
            sector: sectorName,
            location,
            selectedDateInString,
            searchSource: chartType,
            detailedTableTitle: detailedTableTitle,
            detailedTableType: detailedTableType,
            pageHeader: 'Cluster Analytics',
            pageSubHeader: pageSubHeader,
        };
        if (chartType === 'topTenClusterCostbyComputeJob') {
            newState = {
                ...newState,
                contentName: params?.name,
                filters: [
                    {
                        operator: 'AND',
                        field: 'Job Name',
                        value: params?.name,
                    }
                ]
            };
        }
        localStorage.removeItem(detailedTableType);
        const snap = {
            startDate,
            endDate,
            selectedDateInString,
        };
        sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
        navigate('/databricksADB/performance-dashboard', { state: newState });
    };

    const handleCostByComputeInteractiveOnClick = (chartType: any, params: any) => {
        let detailedTableTitle = 'Top 10 Cluster Cost by Compute - Interactive';
        let detailedTableType = 'top-ten-cluster-cost-by-compute-interactive-detail';
        let pageSubHeader = 'Top 10 Cluster Cost by Compute - Interactive';
        let newState: any = {
            startDate,
            endDate,
            env,
            sector: sectorName,
            location,
            selectedDateInString,
            searchSource: chartType,
            detailedTableTitle: detailedTableTitle,
            detailedTableType: detailedTableType,
            pageHeader: 'Cluster Analytics',
            pageSubHeader: pageSubHeader,
        };
        if (chartType === 'topTenClusterCostbyComputeInteractive') {
            newState = {
                ...newState,
                contentName: params?.name,
                filters: [
                    {
                        operator: 'AND',
                        field: 'Cluster Name',
                        value: params?.name,
                    }
                ]
            };
        }
        localStorage.removeItem(detailedTableType);
        const snap = {
            startDate,
            endDate,
            selectedDateInString,
        };
        sessionStorage.setItem(PRESERVE_FILTER_KEY, JSON.stringify(snap));
        navigate('/databricksADB/performance-dashboard', { state: newState });
    };

    const handleProvisionedByCategoryClick = (params: any) => {
        const clickedCategory = params?.name;
        const computeType = (clickedCategory || '').toString().trim().toUpperCase();

        navigate('/databricksADB/cluster_provisioned_by_category', {
            state: {
                dateLabel: 'New Cluster Provisioned By Category',
                startDate,
                endDate,
                env,
                sector,
                location,
                selectedDateInString,
                clusterCategory: clickedCategory,
                computeType,
            }
        });
    };

    return (
        <PageWrapper>
            <Box sx={{ position: 'sticky', top: 64, zIndex: 10 }}>
                <PageHeader
                    title="Cluster Analytics"
                    icon={ClusterIcon}
                    lastSync={formattedLastSync}
                >
                    <FiltersBar
                        location={location}
                        // pass the actual useState setters (not single-arg wrappers)
                        setLocation={setLocation}
                        env={env}
                        setEnv={setEnv}
                        envOptions={envOptions}
                        setEnvOptions={setEnvOptions}
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        handleDateSelect={handleDateSelect}
                        selectedDateInString={selectedDateInString}
                        setSelectedDateInString={setSelectedDateInString}
                        sectorName={sectorName}
                        // typed wrapper to handle SetStateAction<string> (function or value)
                        setSectorName={(v) => {
                            const resolved = typeof v === 'function' ? (v as (prev: string) => string)(sectorName) : v;
                            setSectorName(resolved);
                            setSector(resolved);
                        }}
                        updateHistoryState={updateHistoryState}
                        hasYTD={true}
                        sectorOptionsList={sectorList}
                    />
                </PageHeader>
            </Box>

            <Grid container spacing={2} px={{ xs: 1, md: 2 }} pt={{ xs: 1, md: 2 }} pb={1}>
                <Grid item xs={12} md={4} id="total-count-cluster-by-category" >
                    {loader.clusterType ? (
                        <LoaderComponent size={50} />
                    ) : (
                        <Stack
                            p={2}
                            bgcolor="#ffffff"
                            fontFamily="Montserrat"
                            borderRadius="5px"
                            sx={{
                                height: { xs: '350px', md: '350px' }
                            }}
                        >
                            <Typography variant='widgetTitle' color="#000000">
                                <HighlightedText text="Total Count Cluster By Category" />
                            </Typography>
                            <Box sx={{ flexGrow: 1, mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ReactECharts option={ClusterByCategory} style={{ height: '100%', width: '100%' }} onEvents={{ click: (params: any) => handleCountClusterOnClick('totalCountClusterByCategory', params) }} />
                            </Box>
                        </Stack>
                    )}
                </Grid>
                <Grid item xs={12} md={4} id="cost-spend-by-category">
                    {loader.spentByCategoryType ? (
                        <LoaderComponent size={50} />
                    ) : (
                        <Stack
                            p={2}
                            bgcolor="#ffffff"
                            fontFamily="Montserrat"
                            borderRadius="5px"
                            sx={{
                                height: { xs: '350px', md: '350px' }
                            }}
                        >
                            <Typography variant='widgetTitle' color="#000000">
                                <HighlightedText text="Cost Spend By Category" />
                            </Typography>
                            <Box sx={{ flexGrow: 1, mt: 1, display: 'flex', alignItems: 'center' }}>
                                <ReactECharts option={SpendByCategory} style={{ height: '100%', width: '100%' }} onEvents={{ click: (params: any) => handleCostSpendOnClick('costSpendByCategory', params) }} />
                            </Box>
                        </Stack>
                    )}
                </Grid>
                <Grid item xs={12} md={4} id="total-cost-by-clusters-without-auto-termination">
                    {loader.costType ? (
                        <LoaderComponent size={50} />
                    ) : (
                        <Stack
                            p={2}
                            bgcolor="#ffffff"
                            fontFamily="Montserrat"
                            borderRadius="5px"
                            sx={{
                                height: { xs: '350px', md: '350px' }
                            }}
                        >
                            <Typography variant='widgetTitle' color="#000000">
                                <HighlightedText text="Total Cost by Clusters Without Auto-Termination" />
                            </Typography>
                            <Box sx={{ flexGrow: 1, mt: 1, display: 'flex', alignItems: 'center' }}>
                                <ReactECharts option={CostByCluster} style={{ height: '100%', width: '100%' }} onEvents={{ click: (params: any) => handleCostByClusterOnClick('totalCostByClusterWithAutoTermination', params) }} />
                            </Box>
                        </Stack>
                    )}
                </Grid>
            </Grid>

            <Grid container spacing={2} px={{ xs: 1, md: 2 }} py={{ xs: 1, md: 1.5 }}>
                <Grid item xs={12} md={4} id="clusters-count-without-auto-termination">
                    {loader.clusterType ? (
                        <LoaderComponent size={50} />
                    ) : (
                        <Box
                            onClick={() => handleClusterCountOnClick('clustersCountWithoutAutoTermination')}
                            sx={{ cursor: 'pointer', height: '100%' }}
                        >
                            <NumberWidget
                                title={<HighlightedText text="Clusters Count Without Auto-Termination" />}
                                //tooltipLabel={`${withoutAutoTermCount?.[0]?.value ?? 0}`}
                                tooltipLabel=""
                                number={compactFormatter.format(withoutAutoTermCount?.[0]?.value ?? 0)}
                                units=""
                                isLoading={false}
                                subText={(withoutAutoTermCount?.[0]?.value ?? 0) === 0 ? '' : `${withoutAutoTermCount?.[0]?.name ?? ''} ONLY`}
                                bgColor='#ffffff'
                                color='#000000'
                                valueFontWeight={300}
                            />
                        </Box>
                    )}
                </Grid>
                <Grid item xs={12} md={4} id="new-cluster-provisioned-by-category">
                    {loader.clusterType ? (
                        <LoaderComponent size={50} />
                    ) : (
                        <Stack
                            p={2}
                            bgcolor="#ffffff"
                            fontFamily="Montserrat"
                            borderRadius="5px"
                            sx={{
                                height: { xs: '350px', md: '350px' }
                            }}
                        >
                            <Typography variant='widgetTitle' color="#000000">
                                <HighlightedText text="New Cluster Provisioned By Category" />
                            </Typography>
                            <Box sx={{ flexGrow: 1, mt: 1, display: 'flex', alignItems: 'center' }}>
                                <ReactECharts option={provisionedByCategory} style={{ height: '100%', width: '100%' }} onEvents={{ click: handleProvisionedByCategoryClick }} />
                            </Box>
                        </Stack>
                    )}
                </Grid>
                <Grid item xs={12} md={4} id="clusters-spin-ups-by-category">
                    {loader.SpinsUp ? (
                        <LoaderComponent size={50} />
                    ) : (
                        <Stack
                            p={2}
                            bgcolor="#ffffff"
                            fontFamily="Montserrat"
                            borderRadius="5px"
                            sx={{
                                height: { xs: '350px', md: '350px' }
                            }}
                        >
                            <Typography variant='widgetTitle' color="#000000">
                                <HighlightedText text="Clusters Spin-Ups by Category" />
                            </Typography>
                            <Box sx={{ flexGrow: 1, mt: 1, display: 'flex', alignItems: 'center' }}>
                                <ReactECharts option={ClusterSpinsUps} style={{ height: '100%', width: '100%' }} onEvents={{ click: (params: any) => handleClusterSpinsUpOnClick('clusterSpinUpsByCategory', params) }} />
                            </Box>
                        </Stack>
                    )}
                </Grid>
            </Grid>

            <Grid container spacing={2} px={{ xs: 1, md: 2 }} py={{ xs: 1, md: 1.5 }} pb={{ xs: 1, md: 2 }}>
                <Grid item xs={12} md={6} id="top-10-cluster-cost-by-compute-job">
                    {loader.computeJobType ? (
                        <LoaderComponent size={50} />
                    ) : (
                        <Stack
                            p={2}
                            bgcolor="#ffffff"
                            fontFamily="Montserrat"
                            borderRadius="5px"
                            sx={{
                                height: { xs: '350px', md: '350px' }
                            }}
                        >
                            <Typography variant='widgetTitle' color="#000000">
                                <HighlightedText text="Top 10 Cluster Cost by Compute - Job" />
                            </Typography>
                            <Box sx={{ flexGrow: 1, mt: 1, display: 'flex', alignItems: 'center' }}>
                                <ReactECharts option={ComputeJob} style={{ height: '100%', width: '100%' }} onEvents={{ click: (params: any) => handleCostByComputeJobOnClick('topTenClusterCostbyComputeJob', params) }} />
                            </Box>
                        </Stack>
                    )}
                </Grid>
                <Grid item xs={12} md={6} id="top-10-cluster-cost-by-compute-interactive">
                    {loader.computeInteractiveType ? (
                        <LoaderComponent size={50} />
                    ) : (
                        <Stack
                            p={2}
                            bgcolor="#ffffff"
                            fontFamily="Montserrat"
                            borderRadius="5px"
                            sx={{
                                height: { xs: '350px', md: '350px' }
                            }}
                        >
                            <Typography variant='widgetTitle' color="#000000">
                                <HighlightedText text="Top 10 Cluster Cost by Compute - Interactive" />
                            </Typography>
                            <Box sx={{ flexGrow: 1, mt: 1, display: 'flex', alignItems: 'center' }}>
                                <ReactECharts option={ComputeInteractive} style={{ height: '100%', width: '100%' }} onEvents={{ click: (params: any) => handleCostByComputeInteractiveOnClick('topTenClusterCostbyComputeInteractive', params) }} />
                            </Box>
                        </Stack>
                    )}
                </Grid>
            </Grid>
        </PageWrapper>
    );
};

export default ClusterAnalytics;